/**
 * MilestoneDrawer Component
 * Create / Edit Milestone form
 * BuilderBel Construction Project Management System
 */

import { yupResolver } from '@hookform/resolvers/yup';
import { Calendar, CheckSquare, DollarSign, Flag, Milestone, Plus, Trash2, X } from 'lucide-react';
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useToast } from '../../contexts/ToastContext';
import { useCurrentProjectId } from '../../hooks/useProjectRedux';
import {
  useCreateMilestoneMutation,
  useUpdateMilestoneMutation,
} from '../../services/milestoneApi';
import type {
  CreateMilestoneRequest,
  Milestone as MilestoneType,
  MilestoneType as MilestoneTypeEnum,
  UpdateMilestoneRequest,
} from '../../types/milestone';
import { CurrencyInput, DatePicker, Dropdown, FormSwitch, Input, TextArea } from '../forms';

// ============================================================================
// PROPS
// ============================================================================

export interface MilestoneDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  milestone?: MilestoneType | null;
  mode: 'create' | 'edit' | 'view';
  onSuccess?: () => void;
}

// ============================================================================
// FORM DATA
// ============================================================================

interface CriterionFormItem {
  description: string;
}

interface MilestoneFormData {
  name: string;
  description: string;
  type: MilestoneTypeEnum;
  category: string;
  phase: string;
  baselineDate: string;
  targetDate: string;
  forecastDate: string;
  scheduleActivityId: string;
  progress: number;
  riskLevel: string;
  mitigationPlan: string;
  completionCriteria: CriterionFormItem[];
  requiresSignoff: boolean;
  isPaymentMilestone: boolean;
  paymentAmount: number;
}

// ============================================================================
// VALIDATION SCHEMA
// ============================================================================

const criterionSchema = Yup.object({
  description: Yup.string().required('Criterion description is required'),
});

const validationSchema = Yup.object({
  name: Yup.string()
    .required('Milestone name is required')
    .max(500, 'Name cannot exceed 500 characters'),
  description: Yup.string().optional().max(5000, 'Description cannot exceed 5000 characters'),
  type: Yup.string()
    .oneOf(
      ['project', 'contractual', 'payment', 'permit', 'owner', 'internal'] as const,
      'Invalid milestone type'
    )
    .required('Type is required'),
  category: Yup.string().optional().max(100),
  phase: Yup.string().optional().max(100),
  baselineDate: Yup.string().required('Baseline date is required'),
  targetDate: Yup.string().required('Target date is required'),
  forecastDate: Yup.string().optional(),
  scheduleActivityId: Yup.string().optional(),
  progress: Yup.number().min(0).max(100).required('Progress is required'),
  riskLevel: Yup.string().oneOf(['low', 'medium', 'high', 'critical']).optional(),
  mitigationPlan: Yup.string().optional().max(2000),
  completionCriteria: Yup.array().of(criterionSchema).optional(),
  requiresSignoff: Yup.boolean().optional(),
  isPaymentMilestone: Yup.boolean().optional(),
  paymentAmount: Yup.number().min(0, 'Payment amount cannot be negative').optional(),
});

// ============================================================================
// OPTIONS
// ============================================================================

const TYPE_OPTIONS = [
  { value: 'project', label: 'Project' },
  { value: 'contractual', label: 'Contractual' },
  { value: 'payment', label: 'Payment' },
  { value: 'permit', label: 'Permit' },
  { value: 'owner', label: 'Owner' },
  { value: 'internal', label: 'Internal' },
];

const RISK_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

// ============================================================================
// COMPONENT
// ============================================================================

const MilestoneDrawer: React.FC<MilestoneDrawerProps> = ({
  isOpen,
  onClose,
  milestone,
  mode,
  onSuccess,
}) => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  const [createMilestone, { isLoading: isCreating }] = useCreateMilestoneMutation();
  const [updateMilestone, { isLoading: isUpdating }] = useUpdateMilestoneMutation();

  const isLoading = isCreating || isUpdating;
  const isReadOnly = mode === 'view';

  const defaultValues: MilestoneFormData = {
    name: '',
    description: '',
    type: 'project',
    category: '',
    phase: '',
    baselineDate: '',
    targetDate: '',
    forecastDate: '',
    scheduleActivityId: '',
    progress: 0,
    riskLevel: 'low',
    mitigationPlan: '',
    completionCriteria: [],
    requiresSignoff: false,
    isPaymentMilestone: false,
    paymentAmount: 0,
  };

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
     
  } = useForm<MilestoneFormData>({
    resolver: yupResolver(validationSchema) as any,
    defaultValues,
  });

  const {
    fields: criteriaFields,
    append: appendCriterion,
    remove: removeCriterion,
  } = useFieldArray({ control, name: 'completionCriteria' });

  const isPaymentMilestone = watch('isPaymentMilestone');
  const progressValue = watch('progress');

  // Populate form when editing
  useEffect(() => {
    if (milestone && (mode === 'edit' || mode === 'view')) {
      reset({
        name: milestone.name,
        description: milestone.description || '',
        type: milestone.type,
        category: milestone.category || '',
        phase: milestone.phase || '',
        baselineDate: milestone.baselineDate ? milestone.baselineDate.split('T')[0] : '',
        targetDate: milestone.targetDate ? milestone.targetDate.split('T')[0] : '',
        forecastDate: milestone.forecastDate ? milestone.forecastDate.split('T')[0] : '',
        scheduleActivityId: milestone.scheduleActivityId || '',
        progress: milestone.progress,
        riskLevel: milestone.riskLevel,
        mitigationPlan: milestone.mitigationPlan || '',
        completionCriteria: (milestone.completionCriteria || []).map(c => ({
          description: c.description,
        })),
        requiresSignoff: milestone.requiresSignoff,
        isPaymentMilestone: milestone.isPaymentMilestone,
        paymentAmount: milestone.paymentAmount || 0,
      });
    } else if (mode === 'create') {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [milestone, mode]);

  if (!isOpen) return null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = async (data: any) => {
    const formData = data as MilestoneFormData;

    if (!projectId) {
      toast.error('No project selected');
      return;
    }

    const payload: CreateMilestoneRequest = {
      name: formData.name,
      description: formData.description || undefined,
      type: formData.type as MilestoneTypeEnum,
      category: formData.category || undefined,
      phase: formData.phase || undefined,
      baselineDate: formData.baselineDate,
      targetDate: formData.targetDate,
      forecastDate: formData.forecastDate || undefined,
      scheduleActivityId: formData.scheduleActivityId || undefined,
      completionCriteria: formData.completionCriteria.filter(c => c.description.trim()),
      requiresSignoff: formData.requiresSignoff,
      isPaymentMilestone: formData.isPaymentMilestone,
      paymentAmount: formData.isPaymentMilestone ? formData.paymentAmount : undefined,
      riskLevel: formData.riskLevel as 'low' | 'medium' | 'high' | 'critical',
      mitigationPlan: formData.mitigationPlan || undefined,
    };

    try {
      if (mode === 'create') {
        await createMilestone({ projectId, data: payload }).unwrap();
        toast.success('Milestone created successfully');
      } else if (mode === 'edit' && milestone) {
        const updatePayload: UpdateMilestoneRequest = payload;
        await updateMilestone({
          projectId,
          milestoneId: milestone._id,
          data: updatePayload,
        }).unwrap();
        toast.success('Milestone updated successfully');
      }

      onSuccess?.();
      onClose();
    } catch {
      toast.error(mode === 'create' ? 'Failed to create milestone' : 'Failed to update milestone');
    }
  };

  const drawerContent = (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[60%] animate-in slide-in-from-right">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-8 h-8 bg-brand-50 rounded-lg">
                <Milestone className="h-4 w-4 text-brand-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {mode === 'create'
                    ? 'Add Milestone'
                    : mode === 'edit'
                      ? 'Edit Milestone'
                      : 'Milestone Details'}
                </h2>
                {milestone && <p className="text-xs text-gray-500">{milestone.milestoneNumber}</p>}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Close drawer"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6">
            <form id="milestone-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 flex items-center space-x-2 mb-4">
                  <Flag className="h-4 w-4 text-brand-600" />
                  <span>Basic Information</span>
                </h3>
                <div className="space-y-4">
                  <Input
                    label="Milestone Name *"
                    {...register('name')}
                    error={errors.name?.message}
                    placeholder="Enter milestone name"
                    disabled={isReadOnly}
                  />

                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextArea
                        label="Description"
                        value={field.value}
                        onChange={field.onChange}
                        error={errors.description?.message}
                        placeholder="Describe this milestone..."
                        rows={3}
                        disabled={isReadOnly}
                      />
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <Controller
                      name="type"
                      control={control}
                      render={({ field }) => (
                        <Dropdown
                          label="Type *"
                          options={TYPE_OPTIONS}
                          value={field.value}
                          onChange={val => field.onChange(val)}
                          error={errors.type?.message}
                          disabled={isReadOnly}
                        />
                      )}
                    />

                    <Input
                      label="Category"
                      {...register('category')}
                      error={errors.category?.message}
                      placeholder="e.g., Design, Construction"
                      disabled={isReadOnly}
                    />
                  </div>

                  <Input
                    label="Phase"
                    {...register('phase')}
                    error={errors.phase?.message}
                    placeholder="e.g., Phase 1, Pre-Construction"
                    disabled={isReadOnly}
                  />
                </div>
              </div>

              {/* Dates */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 flex items-center space-x-2 mb-4">
                  <Calendar className="h-4 w-4 text-brand-600" />
                  <span>Dates</span>
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <Controller
                    name="baselineDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Baseline Date *"
                        value={field.value}
                        onChange={val => field.onChange(val)}
                        error={errors.baselineDate?.message}
                        disabled={isReadOnly}
                      />
                    )}
                  />

                  <Controller
                    name="targetDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Target Date *"
                        value={field.value}
                        onChange={val => field.onChange(val)}
                        error={errors.targetDate?.message}
                        disabled={isReadOnly}
                      />
                    )}
                  />

                  <Controller
                    name="forecastDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Forecast Date"
                        value={field.value}
                        onChange={val => field.onChange(val)}
                        error={errors.forecastDate?.message}
                        disabled={isReadOnly}
                      />
                    )}
                  />
                </div>
              </div>

              {/* Progress */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-gray-500">Progress</label>
                  <span className="text-xs font-semibold text-gray-700">{progressValue}%</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  {...register('progress', { valueAsNumber: true })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-brand-600"
                  disabled={isReadOnly}
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>0%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
              </div>

              {/* Risk */}
              <div>
                <div className="grid grid-cols-2 gap-4">
                  <Controller
                    name="riskLevel"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        label="Risk Level"
                        options={RISK_OPTIONS}
                        value={field.value}
                        onChange={val => field.onChange(val)}
                        error={errors.riskLevel?.message}
                        disabled={isReadOnly}
                      />
                    )}
                  />
                </div>

                <div className="mt-4">
                  <Controller
                    name="mitigationPlan"
                    control={control}
                    render={({ field }) => (
                      <TextArea
                        label="Mitigation Plan"
                        value={field.value}
                        onChange={field.onChange}
                        error={errors.mitigationPlan?.message}
                        placeholder="Describe the risk mitigation plan..."
                        rows={2}
                        disabled={isReadOnly}
                      />
                    )}
                  />
                </div>
              </div>

              {/* Completion Criteria */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
                    <CheckSquare className="h-4 w-4 text-brand-600" />
                    <span>Completion Criteria</span>
                  </h3>
                  {!isReadOnly && (
                    <button
                      type="button"
                      onClick={() => appendCriterion({ description: '' })}
                      className="flex items-center space-x-1 text-xs text-brand-600 hover:text-brand-700 font-medium"
                    >
                      <Plus className="h-3 w-3" />
                      <span>Add Criterion</span>
                    </button>
                  )}
                </div>

                {criteriaFields.length === 0 && (
                  <p className="text-xs text-gray-400 italic">No completion criteria defined.</p>
                )}

                <div className="space-y-2">
                  {criteriaFields.map((field, index) => (
                    <div key={field.id} className="flex items-start space-x-2">
                      <div className="flex-1">
                        <Input
                          {...register(`completionCriteria.${index}.description`)}
                          placeholder={`Criterion ${index + 1}...`}
                          error={errors.completionCriteria?.[index]?.description?.message}
                          disabled={isReadOnly}
                        />
                      </div>
                      {!isReadOnly && (
                        <button
                          type="button"
                          onClick={() => removeCriterion(index)}
                          className="mt-2 p-1.5 text-gray-400 hover:text-red-600 rounded transition-colors"
                          aria-label="Remove criterion"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Sign-off Toggle */}
              <Controller
                name="requiresSignoff"
                control={control}
                render={({ field }) => (
                  <FormSwitch
                    label="Requires sign-off before completion"
                    checked={!!field.value}
                    onChange={field.onChange}
                    disabled={isReadOnly}
                    size="sm"
                  />
                )}
              />

              {/* Payment Milestone */}
              <div>
                <Controller
                  name="isPaymentMilestone"
                  control={control}
                  render={({ field }) => (
                    <FormSwitch
                      label="Payment Milestone"
                      checked={!!field.value}
                      onChange={field.onChange}
                      disabled={isReadOnly}
                      size="sm"
                    />
                  )}
                />

                {isPaymentMilestone && (
                  <div className="ml-7">
                    <Controller
                      name="paymentAmount"
                      control={control}
                      render={({ field }) => (
                        <CurrencyInput
                          label="Payment Amount"
                          value={field.value}
                          onChange={val => field.onChange(val)}
                          error={errors.paymentAmount?.message}
                          disabled={isReadOnly}
                        />
                      )}
                    />
                  </div>
                )}
              </div>
            </form>
          </div>

          {/* Footer */}
          {!isReadOnly && (
            <div className="border-t border-gray-200 px-8 py-5 bg-gray-50 flex justify-end space-x-3 rounded-b-2xl">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="milestone-form"
                className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors disabled:bg-brand-200 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading
                  ? mode === 'create'
                    ? 'Creating...'
                    : 'Saving...'
                  : mode === 'create'
                    ? 'Create Milestone'
                    : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return createPortal(drawerContent, document.body);
};

export default MilestoneDrawer;
